import { IFRAME_HEIGHT_MAX, IFRAME_HEIGHT_MIN } from "@repo/shared";
// Local translations for main dialog
export const T = {
  en_GB: {
    // Dialog header
    dialogTitle: "Component Settings",
    dialogDescription: "Configure settings and conditional display rules for this component",

    // Buttons
    cancel: "Cancel",
    save: "Save",

    // Component labels
    componentPrefix: "Component:",
    fieldPrefix: "Field:",

    // Tabs
    componentSettingsTab: "Component Settings",
    fieldsTab: "Fields",
    conditionalDisplayTab: "Conditional Display",
    htmlTab: "HTML Code",

    // Text block
    content: "Content",
    textBlockPlaceholder:
      "Write the text — a heading, **bold**, *italic*, lists and links are supported",
    textBlockHelp:
      "One heading style and paragraphs. Use the toolbar or type # at the start of a line for a heading; it renders like every other component header.",

    // Spacer
    heightPixels: "Height (pixels)",
    spacerHelp: "How much vertical space to add (recommended: 20-40px)",

    // Goal input (QP-187)
    showGoalDescription: "Show description field",
    showGoalDescriptionHelp: "Adds an optional goal-description textarea (max 150 characters)",

    // QP-179 — sustainability status
    sustainabilityAllowOverride: "Allow override (advisor can accept a deviation)",
    sustainabilityAllowOverrideHelp:
      "When off, a deviation must be resolved by changing the client's preferences or the portfolio — the advisor cannot accept it.",

    // QL-13 — quiz
    quizSetup: "Quiz",
    quizSetupHelp: "The client must pass this quiz before the flow can be submitted.",

    // QP-172 — portfolio list
    portfolioListShowAmount: "Show amount",
    portfolioListShowFrequency: "Show frequency",
    portfolioListShowDates: "Show dates",

    // Conditional visibility
    conditionalLabel: "Enable Conditional Display",
    conditionalDescription: "Show this component only when a condition is met",
    fieldConditionalLabel: "Conditional Display",
    fieldConditionalDescription: "Show this field only when a condition is met",

    // Validation errors
    pleaseSelectTimeRange: "Please select at least one time range",
    textContentRequired: "Text content is required",
    spacerHeightPositive: "Spacer height must be positive",
    youtubeUrlRequired: "YouTube URL is required",
    iframeUrlInvalid: "Please enter an absolute https:// web page URL",
    iframeUrlAppOrigin: "This URL points to the app itself and cannot be shown here",
    iframeHeightInvalid: `Height must be between ${IFRAME_HEIGHT_MIN} and ${IFRAME_HEIGHT_MAX} pixels`,
    imageRequired: "Image is required",
    embedHtmlRequired: "Embed HTML is required",
    modalButtonLabelRequired: "Button label is required",
    modalTitleRequired: "Popup title is required",
    modalContentRequired: "Popup content is required",
    fileDocumentRequired: "Document is required",
    selectAtLeastOneDisplay: "Please select at least one display option: Show Chart or Show Table",
    selectAtLeastOneDisplayOption: "Please select at least one display option",
    selectAtLeastOnePortfolioType: "Please select at least one allowed portfolio type",
    failedToUpdate: "Failed to update component",
    errorOccurred: "An error occurred while updating the component",
    required: "*",
  },
  nb_NO: {
    // Dialog header
    dialogTitle: "Komponentinnstillinger",
    dialogDescription: "Konfigurer innstillinger og betingede visningsregler for denne komponenten",

    // Buttons
    cancel: "Avbryt",
    save: "Lagre",

    // Component labels
    componentPrefix: "Komponent:",
    fieldPrefix: "Felt:",

    // Tabs
    componentSettingsTab: "Komponentinnstillinger",
    fieldsTab: "Felter",
    conditionalDisplayTab: "Betinget visning",
    htmlTab: "HTML-kode",

    // Text block
    content: "Innhold",
    textBlockPlaceholder: "Skriv teksten — overskrift, **fet**, *kursiv*, lister og lenker støttes",
    textBlockHelp:
      "Én overskriftsstil og avsnitt. Bruk verktøylinjen eller skriv # først på linjen for en overskrift; den vises som alle andre komponentoverskrifter.",

    // Spacer
    heightPixels: "Høyde (piksler)",
    spacerHelp: "Hvor mye vertikal plass som skal legges til (anbefalt: 20-40px)",

    // Goal input (QP-187)
    showGoalDescription: "Vis beskrivelsesfelt",
    showGoalDescriptionHelp: "Legger til et valgfritt beskrivelsesfelt for målet (maks 150 tegn)",

    // QP-179 — sustainability status
    sustainabilityAllowOverride: "Tillat overstyring (rådgiver kan akseptere avvik)",
    sustainabilityAllowOverrideHelp:
      "Når av, må et avvik løses ved å endre kundens preferanser eller porteføljen — rådgiveren kan ikke akseptere det.",

    // QL-13 — quiz
    quizSetup: "Quiz",
    quizSetupHelp: "Kunden må bestå denne quizen før flyten kan sendes inn.",

    // QP-172 — portfolio list
    portfolioListShowAmount: "Vis beløp",
    portfolioListShowFrequency: "Vis frekvens",
    portfolioListShowDates: "Vis datoer",

    // Conditional visibility
    conditionalLabel: "Aktiver betinget visning",
    conditionalDescription: "Vis denne komponenten kun når en betingelse er oppfylt",
    fieldConditionalLabel: "Betinget visning",
    fieldConditionalDescription: "Vis dette feltet kun når en betingelse er oppfylt",

    // Validation errors
    pleaseSelectTimeRange: "Vennligst velg minst ett tidsrom",
    textContentRequired: "Tekstinnhold er påkrevd",
    spacerHeightPositive: "Mellomromshøyde må være positiv",
    youtubeUrlRequired: "YouTube-URL er påkrevd",
    iframeUrlInvalid: "Vennligst skriv inn en absolutt https://-nettside-URL",
    iframeUrlAppOrigin: "Denne adressen peker til selve appen og kan ikke vises her",
    iframeHeightInvalid: `Høyden må være mellom ${IFRAME_HEIGHT_MIN} og ${IFRAME_HEIGHT_MAX} piksler`,
    imageRequired: "Bilde er påkrevd",
    embedHtmlRequired: "Innebygd HTML er påkrevd",
    modalButtonLabelRequired: "Knappetekst er påkrevd",
    modalTitleRequired: "Popup-tittel er påkrevd",
    modalContentRequired: "Popup-innhold er påkrevd",
    fileDocumentRequired: "Dokument er påkrevd",
    selectAtLeastOneDisplay:
      "Vennligst velg minst ett visningsalternativ: Vis diagram eller Vis tabell",
    selectAtLeastOneDisplayOption: "Vennligst velg minst ett visningsalternativ",
    selectAtLeastOnePortfolioType: "Vennligst velg minst én tillatt porteføljetype",
    failedToUpdate: "Kunne ikke oppdatere komponent",
    errorOccurred: "Det oppstod en feil under oppdatering av komponenten",
    required: "*",
  },
} as const;
